/**
 * Client HTTP minimal verso il backend.
 *
 * - Inietta automaticamente l'header `Authorization: Bearer <token>` se c'è
 *   un token in storage.
 * - Su 401: pulisce il token e emette evento `auth:unauthorized`
 *   (escluso il flusso di login dove l'errore va mostrato a video).
 * - Su altri errori: solleva ApiError con `status` e `detail`.
 */
import { authStorage } from './auth';
import type {
  AuthSuccessDto,
  AuditChainVerificationDto,
  AuditLogDto,
  BatchCreateInput,
  BatchDto,
  BatchTraceabilityDto,
  BatchUpdateInput,
  CashExpenseCreateInput,
  CashExpenseDto,
  CashExpenseUpdateInput,
  DailyCashSummaryDto,
  DailySaleCreateInput,
  AcceptMembershipInviteInput,
  DailySaleDto,
  DailySaleUpdateInput,
  AddMemberInput,
  ChangePasswordInput,
  DeclaredExperienceInput,
  MembershipInvitePublicDto,
  ApplicationDto,
  JobOpeningFeedDto,
  JobOpeningMineDto,
  JobPreferencesInput,
  EmployeeNoteDto,
  HoursRowDto,
  LatenessDto,
  LatenessInput,
  MeTimeClockDto,
  MyApplicationDto,
  NoteInput,
  NetworkSummaryDto,
  OpeningCreateInput,
  PunchInput,
  PunchResultDto,
  TeamRecordDetailDto,
  TeamRecordDto,
  TimeClockConfigDto,
  TimeClockSettingsDto,
  TimeClockSettingsInput,
  TodayRowDto,
  ReferenceGrantInput,
  ReferenceRequestDto,
  TalentRoleDto,
  TalentCandidateDto,
  TalentMeDto,
  TalentProfileMeDto,
  TalentProfileUpdateInput,
  TalentSessionDto,
  CompanyMiniDto,
  ContractCategory,
  ContractCreateInput,
  ContractDto,
  ContractsSummaryDto,
  ContractUpdateInput,
  DepartmentCreateInput,
  DepartmentDto,
  DepartmentUpdateInput,
  EquipmentCreateInput,
  EquipmentDto,
  EquipmentType,
  EquipmentUpdateInput,
  CleaningArea,
  CleaningDashboard,
  CleaningLogCreateInput,
  CleaningLogDto,
  CleaningTaskCreateInput,
  CleaningTaskDto,
  CleaningTaskUpdateInput,
  HaccpCertificateCreateInput,
  HaccpCertificateDto,
  HaccpCertificatesDashboard,
  HaccpCertificateUpdateInput,
  HaccpDashboardDto,
  TemperatureReadingCreateInput,
  TemperatureReadingDto,
  InvoiceConfirmInput,
  InvoiceConfirmResult,
  InvoiceDetailDto,
  InvoiceListDto,
  InvoiceStatus,
  EmployeeAdvanceCreateInput,
  EmployeeAdvanceDto,
  EmployeeAdvanceUpdateInput,
  CrossLocationBusyDto,
  DocumentCategory,
  EmployeeCreateInput,
  EmployeeDepartment,
  EmployeeDto,
  EmployeeDocumentDto,
  EmployeeUpdateInput,
  ExpenseCategory,
  ExpiryStatus,
  LocationCreateInput,
  LocationFullDto,
  LocationUpdateInput,
  MemberDto,
  MonthlyPayrollSummaryDto,
  AlertDto,
  AlertSeverity,
  AlertType,
  AlertUnreadCountDto,
  NotificationSummaryDto,
  FlowForecastDto,
  LocalEventCreateInput,
  LocalEventDto,
  LocalEventUpdateInput,
  ForecastSummaryDto,
  MenuEngineeringSummaryDto,
  NegotiationEmailDto,
  RecipeDto,
  RecipeUpsertInput,
  SourcingSummaryDto,
  PayrollPeriod,
  PayrollPeriodSummaryDto,
  ShiftKind,
  ShiftServiceCreateInput,
  ShiftServiceDto,
  ShiftServiceUpdateInput,
  UpdateCompanyInput,
  RecurringShiftInput,
  RecurringShiftResultDto,
  UpdateMembershipInput,
  UpdateProfileInput,
  UserDto,
  WorkShiftCreateInput,
  WorkShiftDto,
  WorkShiftUpdateInput,
  NotifyShiftsResult,
  ShiftNoticeDto,
  NotificationPrefsDto,
  BlastChillCompleteInput,
  BlastChillCycleDto,
  BlastChillStartInput,
  LabelFormat,
  LocationClosureCreateInput,
  LocationClosureDto,
  InviteCreateInput,
  InviteDto,
  InvitePublicDto,
  LoginInput,
  LoginNeedsCompanyDto,
  MeDto,
  MovementCreateInput,
  MovementDto,
  MovementType,
  MovementUpdateInput,
  MyMembershipsDto,
  ParsedTaxPlanDto,
  PaymentMethod,
  PaymentsScheduleDto,
  TaxInstallmentDto,
  TaxPlanDto,
  PayrollSimulationRequest,
  PayrollSimulationResponse,
  PayrollSimulatorMeta,
  ParsedProductsDto,
  BulkProductInput,
  BulkProductsResult,
  ProductCreateInput,
  ProductDto,
  ProductUpdateInput,
  RegisterInput,
  SensorChannelCreateInput,
  SensorChannelDto,
  SensorChannelUpdateInput,
  SensorRawReadingDto,
  ServiceType,
  ParsedSupplierDto,
  SupplierCategory,
  SupplierCreateInput,
  SupplierDto,
  SupplierUpdateInput,
  SwitchCompanyInput,
  SwitchLocationInput,
  CreateMenuItemsResultDto,
  CsvImportResult,
  PosClosureImportDetailDto,
  PosClosureImportDto,
  PosCredentialsCreateInput,
  PosCredentialsDto,
  PosCredentialsUpdateInput,
  PosEmailPollResultDto,
  PosProviderDto,
  PosSyncResultDto,
  ApplyMatchesResultDto,
  MatchSuggestionDto,
  ParsedPriceListDto,
  PriceListCsvResult,
  PriceListItemCreateInput,
  PriceListItemDto,
  PriceListItemUpdateInput,
  PermissionsCatalogDto,
  ProductSourcingDto,
  GeneratedReplyDto,
  ReviewDto,
  ReviewCreateInput,
  ReviewReplyConfigDto,
  ReviewReplyConfigUpdateInput,
  ReviewReplyUpdateInput,
  RoleCreateInput,
  RoleDto,
  RoleUpdateInput,
  SupplierPriceListCreateInput,
  SupplierPriceListDto,
  SupplierPriceListUpdateInput,
  MenuItemCreateInput,
  MenuItemDto,
  MenuItemSaleDto,
  MenuItemSalesBulkInput,
  MenuItemUpdateInput,
  CostDiagnosticsDashboardDto,
  MultiLocationDashboardDto,
  HoldingDto,
  CompanyInHoldingDto,
  HoldingDashboardDto,
  CompaniesOverviewDto,
  SalesAnalyticsDashboardDto,
  TraceabilitySearchResult,
  TransferCreateInput,
  TransferDto,
  CsvImportReport,
  JobRoleCreateInput,
  JobRoleDto,
  JobRoleUpdateInput,
  VacationOverlapResponseDto,
  VacationRequestCreateInput,
  VacationRequestDecisionInput,
  VacationRequestDto,
  VacationRequestStatus,
  VacationRequestUpdateInput,
  VacationWindowCreateInput,
  VacationWindowDto,
  VacationWindowUpdateInput,
  ReservationDto,
  ReservationInput,
  StripeStatusDto,
  KitchenTaskDto,
  KitchenTaskInput,
  KitchenTaskTemplateDto,
  KitchenTaskTemplateInput,
  DishWasteDto,
  DishWasteInput,
  DishWasteSummaryItem,
  OrderDto,
  OrderDetailDto,
  OrderPaymentInput,
  OrderPaymentResultDto,
  CloseResultDto,
  OrderLineDto,
  OrderLineState,
  KdsLineDto,
  KdsOrderGroupDto,
  DiningTableDto,
  DiningTableState,
  MenuAvailabilityDto,
  VoidRequestDto,
  VoidRequestResultDto,
  OrderLineVoidDto,
  LocationModuleDto,
} from './types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Etichette italiane per i campi più comuni (loc finale → label leggibile).
const FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  password: 'Password',
  full_name: 'Nome e cognome',
  name: 'Nome',
  role: 'Ruolo',
  location_scope: 'Locali',
  department: 'Reparto',
  hourly_rate: 'Paga oraria',
  monthly_salary: 'Stipendio mensile',
  shift_date: 'Data',
  hours: 'Ore',
  amount: 'Importo',
};

/** Traduce una singola voce di errore di validazione FastAPI in italiano. */
function formatValidationItem(e: {
  type?: string;
  msg?: string;
  loc?: unknown[];
  ctx?: Record<string, unknown>;
}): string {
  const rawField = Array.isArray(e.loc) ? String(e.loc[e.loc.length - 1] ?? '') : '';
  const field = FIELD_LABELS[rawField] ?? rawField;
  const n = e.ctx?.min_length ?? e.ctx?.max_length;
  switch (e.type) {
    case 'string_too_short':
      return `${field}: deve avere almeno ${n} caratteri.`;
    case 'string_too_long':
      return `${field}: è troppo lungo (massimo ${n} caratteri).`;
    case 'missing':
      return `${field}: campo obbligatorio.`;
    case 'value_error':
      if (rawField === 'email') return 'Email: indirizzo non valido.';
      return `${field}: valore non valido.`;
    case 'greater_than':
    case 'greater_than_equal':
      return `${field}: valore non valido.`;
    default:
      return field ? `${field}: ${e.msg ?? 'valore non valido'}` : (e.msg ?? 'Dato non valido');
  }
}

/** Costruisce un messaggio leggibile dal `detail` di una risposta d'errore.
 *  FastAPI 422 restituisce un ARRAY di errori di validazione: prima li
 *  scartavamo mostrando solo "HTTP 422". */
function formatErrorDetail(detail: unknown, status: number): string {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const parts = detail.map((e) => formatValidationItem(e)).filter(Boolean);
    if (parts.length) return parts.join(' ');
  }
  return `HTTP ${status}`;
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  silent401?: boolean;
  searchParams?: Record<string, string | number | boolean | undefined | null>;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const token = authStorage.getToken();
  const isFormData =
    typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const headers: Record<string, string> = {};
  // Per FormData lasciamo che fetch setti il Content-Type con boundary corretto.
  if (!isFormData) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let url = `${API_BASE_URL}${path}`;
  if (opts.searchParams) {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.searchParams)) {
      if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
    }
    const qs = sp.toString();
    if (qs) url += (path.includes('?') ? '&' : '?') + qs;
  }

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body:
      opts.body === undefined
        ? undefined
        : isFormData
          ? (opts.body as FormData)
          : JSON.stringify(opts.body),
  });

  if (res.status === 204) return undefined as T;

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* response non JSON, body resta null */
  }

  if (!res.ok) {
    if (res.status === 401 && !opts.silent401) {
      authStorage.clear();
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    const detail = (body as { detail?: unknown })?.detail;
    const msg = formatErrorDetail(detail, res.status);
    throw new ApiError(res.status, msg, detail);
  }

  return body as T;
}

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export interface HealthResponse {
  status: string;
  service: string;
  version: string;
  environment: string;
  /** Registrazione pubblica self-service attiva. Se false la UI di
   *  /register mostra un messaggio "solo su invito". */
  public_registration?: boolean;
}

export const fetchHealth = (): Promise<HealthResponse> => request('/health');

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const apiRegister = (input: RegisterInput): Promise<AuthSuccessDto> =>
  request('/auth/register', { method: 'POST', body: input, silent401: true });

export const apiLogin = (input: LoginInput): Promise<AuthSuccessDto | TalentSessionDto> =>
  request('/auth/login', { method: 'POST', body: input, silent401: true });

export const apiSwitchCompany = (input: SwitchCompanyInput): Promise<AuthSuccessDto> =>
  request('/auth/switch-company', { method: 'POST', body: input });

export const apiSwitchLocation = (input: SwitchLocationInput): Promise<AuthSuccessDto> =>
  request('/auth/switch-location', { method: 'POST', body: input });

export const apiMe = (): Promise<MeDto | TalentMeDto> => request('/auth/me');

// --- Inviti onboarding nuove aziende ---------------------------------------

/** Valida un token d'invito (pubblico, no auth). silent401 per non sloggare. */
export const apiGetInvite = (token: string): Promise<InvitePublicDto> =>
  request(`/auth/invite/${encodeURIComponent(token)}`, { silent401: true });

/** Super-admin: genera un invito → ritorna l'invito col token. */
export const apiCreateInvite = (input: InviteCreateInput): Promise<InviteDto> =>
  request('/admin/invites', { method: 'POST', body: input });

/** Super-admin: lista inviti (più recenti prima). */
export const apiListInvites = (): Promise<InviteDto[]> =>
  request('/admin/invites');

/** Super-admin: revoca un invito non ancora usato. */
export const apiDeleteInvite = (id: number): Promise<void> =>
  request(`/admin/invites/${id}`, { method: 'DELETE' });

export const apiMyCompanies = (): Promise<MyMembershipsDto> => request('/companies/me');

export const apiCompaniesOverview = (params: {
  year: number;
  month?: number;
}): Promise<CompaniesOverviewDto> => {
  const qs = new URLSearchParams();
  qs.set('year', String(params.year));
  if (params.month !== undefined) qs.set('month', String(params.month));
  return request(`/companies/overview?${qs.toString()}`);
};

/** Helper per riconoscere il 409 "choose_company" su /auth/login. */
export function isChooseCompanyError(
  err: unknown,
): err is ApiError & { detail: LoginNeedsCompanyDto } {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const d = err.detail as Partial<LoginNeedsCompanyDto> | undefined;
  return d?.detail === 'choose_company' && Array.isArray(d?.memberships);
}

// ---------------------------------------------------------------------------
// Suppliers
// ---------------------------------------------------------------------------

export const apiListSuppliers = (params?: {
  category?: SupplierCategory;
  active?: boolean;
}): Promise<SupplierDto[]> =>
  request('/suppliers', { searchParams: params });

export const apiGetSupplier = (id: number): Promise<SupplierDto> =>
  request(`/suppliers/${id}`);

export const apiCreateSupplier = (input: SupplierCreateInput): Promise<SupplierDto> =>
  request('/suppliers', { method: 'POST', body: input });

/** Legge l'anagrafica fornitore da un documento (PDF/foto) via OCR.
 *  Non crea nulla: serve a pre-compilare il form nuovo-fornitore. */
export const apiParseSupplierDocument = async (
  file: File,
): Promise<ParsedSupplierDto> => {
  const form = new FormData();
  form.append('file', file);
  return request('/suppliers/parse-document', { method: 'POST', body: form });
};

export const apiUpdateSupplier = (
  id: number,
  input: SupplierUpdateInput,
): Promise<SupplierDto> =>
  request(`/suppliers/${id}`, { method: 'PATCH', body: input });

export const apiDeleteSupplier = (id: number): Promise<void> =>
  request(`/suppliers/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export const apiListProducts = (params?: {
  category?: SupplierCategory;
  active?: boolean;
  supplier_id?: number;
  search?: string;
}): Promise<ProductDto[]> =>
  request('/products', { searchParams: params });

export const apiGetProduct = (id: number): Promise<ProductDto> =>
  request(`/products/${id}`);

export const apiCreateProduct = (input: ProductCreateInput): Promise<ProductDto> =>
  request('/products', { method: 'POST', body: input });

export const apiUpdateProduct = (
  id: number,
  input: ProductUpdateInput,
): Promise<ProductDto> =>
  request(`/products/${id}`, { method: 'PATCH', body: input });

export const apiDeleteProduct = (id: number): Promise<void> =>
  request(`/products/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Batches
// ---------------------------------------------------------------------------

export const apiListBatches = (params?: {
  product_id?: number;
  supplier_id?: number;
  active?: boolean;
  expiry_status?: ExpiryStatus;
  received_from?: string;
  received_to?: string;
}): Promise<BatchDto[]> =>
  request('/batches', { searchParams: params });

export const apiGetBatch = (id: number): Promise<BatchDto> =>
  request(`/batches/${id}`);

export const apiCreateBatch = (input: BatchCreateInput): Promise<BatchDto> =>
  request('/batches', { method: 'POST', body: input });

export const apiUpdateBatch = (
  id: number,
  input: BatchUpdateInput,
): Promise<BatchDto> =>
  request(`/batches/${id}`, { method: 'PATCH', body: input });

export const apiDeleteBatch = (id: number): Promise<void> =>
  request(`/batches/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Tracciabilità + etichette (Pilastro 5.3)
// ---------------------------------------------------------------------------

export const apiGetBatchTrace = (id: number): Promise<BatchTraceabilityDto> =>
  request(`/traceability/batches/${id}`);

export const apiSearchLot = (code: string): Promise<TraceabilitySearchResult> =>
  request(`/traceability/search?code=${encodeURIComponent(code)}`);

/**
 * URL assoluto al PDF etichetta del lotto (richiede header Authorization,
 * quindi la apriamo con fetch + blob anziché un <a href>).
 */
export function batchLabelPdfUrl(
  id: number,
  fmt: LabelFormat = 'brother_62',
  copies = 1,
): string {
  return `${API_BASE_URL}/batches/${id}/label.pdf?fmt=${fmt}&copies=${copies}`;
}

/**
 * Apre il PDF etichetta in una nuova tab (con header Authorization).
 * Usa fetch → blob → window.open per gestire la Bearer auth.
 */
export async function openBatchLabelPdf(
  id: number,
  fmt: LabelFormat = 'brother_62',
  copies = 1,
): Promise<void> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(batchLabelPdfUrl(id, fmt, copies), { headers });
  if (!res.ok) {
    throw new ApiError(res.status, `PDF non disponibile (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank');
  // Revoca dopo 60s — il browser deve aver caricato il PDF
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  if (!opened) {
    // Pop-up bloccato — forzo download
    const a = document.createElement('a');
    a.href = url;
    a.download = `etichetta-L${String(id).padStart(6, '0')}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

// ---------------------------------------------------------------------------
// Movements
// ---------------------------------------------------------------------------

export const apiListMovements = (params?: {
  batch_id?: number;
  type?: MovementType;
  from_date?: string;
  to_date?: string;
}): Promise<MovementDto[]> =>
  request('/movements', { searchParams: params });

export const apiGetMovement = (id: number): Promise<MovementDto> =>
  request(`/movements/${id}`);

export const apiCreateMovement = (input: MovementCreateInput): Promise<MovementDto> =>
  request('/movements', { method: 'POST', body: input });

export const apiUpdateMovement = (
  id: number,
  input: MovementUpdateInput,
): Promise<MovementDto> =>
  request(`/movements/${id}`, { method: 'PATCH', body: input });

export const apiDeleteMovement = (id: number): Promise<void> =>
  request(`/movements/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Cassa
// ---------------------------------------------------------------------------

export const apiListSales = (params?: {
  from_date?: string;
  to_date?: string;
  service?: ServiceType;
}): Promise<DailySaleDto[]> =>
  request('/cash/sales', { searchParams: params });

export const apiGetSale = (id: number): Promise<DailySaleDto> =>
  request(`/cash/sales/${id}`);

export const apiCreateSale = (input: DailySaleCreateInput): Promise<DailySaleDto> =>
  request('/cash/sales', { method: 'POST', body: input });

export const apiUpdateSale = (
  id: number,
  input: DailySaleUpdateInput,
): Promise<DailySaleDto> =>
  request(`/cash/sales/${id}`, { method: 'PATCH', body: input });

export const apiDeleteSale = (id: number): Promise<void> =>
  request(`/cash/sales/${id}`, { method: 'DELETE' });

export const apiListExpenses = (params?: {
  from_date?: string;
  to_date?: string;
  category?: ExpenseCategory;
  supplier_id?: number;
}): Promise<CashExpenseDto[]> =>
  request('/cash/expenses', { searchParams: params });

export const apiGetExpense = (id: number): Promise<CashExpenseDto> =>
  request(`/cash/expenses/${id}`);

export const apiCreateExpense = (input: CashExpenseCreateInput): Promise<CashExpenseDto> =>
  request('/cash/expenses', { method: 'POST', body: input });

export const apiUpdateExpense = (
  id: number,
  input: CashExpenseUpdateInput,
): Promise<CashExpenseDto> =>
  request(`/cash/expenses/${id}`, { method: 'PATCH', body: input });

export const apiDeleteExpense = (id: number): Promise<void> =>
  request(`/cash/expenses/${id}`, { method: 'DELETE' });

export const apiGetDailySummary = (date: string): Promise<DailyCashSummaryDto> =>
  request('/cash/summary', { searchParams: { date } });

// ---------------------------------------------------------------------------
// Personnel
// ---------------------------------------------------------------------------

export const apiListEmployees = (params?: {
  active?: boolean;
  department?: EmployeeDepartment;
}): Promise<EmployeeDto[]> =>
  request('/employees', { searchParams: params });

export const apiGetEmployee = (id: number): Promise<EmployeeDto> =>
  request(`/employees/${id}`);

/** Restituisce l'Employee della location corrente con la mia email.
 * 404 se non c'è ancora una anagrafica collegata. */
export const apiGetMyEmployeeProfile = (): Promise<EmployeeDto> =>
  request('/employees/me');

/** Staff segnala scarso/finito su un prodotto. */
export const apiSignalStock = (
  productId: number,
  input: { kind: 'low' | 'out'; note?: string | null },
): Promise<ProductDto> =>
  request(`/products/${productId}/signal-stock`, {
    method: 'POST',
    body: input,
  });

/** Owner/manager azzera la segnalazione (dopo aver riordinato). */
export const apiClearStockSignal = (productId: number): Promise<ProductDto> =>
  request(`/products/${productId}/signal-stock`, { method: 'DELETE' });

export const apiCreateEmployee = (input: EmployeeCreateInput): Promise<EmployeeDto> =>
  request('/employees', { method: 'POST', body: input });

export const apiUpdateEmployee = (
  id: number,
  input: EmployeeUpdateInput,
): Promise<EmployeeDto> =>
  request(`/employees/${id}`, { method: 'PATCH', body: input });

export const apiDeleteEmployee = (id: number): Promise<void> =>
  request(`/employees/${id}`, { method: 'DELETE' });

export const apiListShifts = (params?: {
  from_date?: string;
  to_date?: string;
  employee_id?: number;
  service_id?: number;
  kind?: ShiftKind;
}): Promise<WorkShiftDto[]> =>
  request('/shifts', { searchParams: params });

export const apiGetShift = (id: number): Promise<WorkShiftDto> =>
  request(`/shifts/${id}`);

export const apiCreateShift = (input: WorkShiftCreateInput): Promise<WorkShiftDto> =>
  request('/shifts', { method: 'POST', body: input });

export const apiCrossLocationBusy = (
  date: string,
): Promise<CrossLocationBusyDto[]> =>
  request('/shifts/cross-location-busy', { searchParams: { date } });

export const apiCreateRecurringShifts = (
  input: RecurringShiftInput,
): Promise<RecurringShiftResultDto> =>
  request('/shifts/recurring', { method: 'POST', body: input });

export const apiUpdateShift = (
  id: number,
  input: WorkShiftUpdateInput,
): Promise<WorkShiftDto> =>
  request(`/shifts/${id}`, { method: 'PATCH', body: input });

export const apiDeleteShift = (id: number): Promise<void> =>
  request(`/shifts/${id}`, { method: 'DELETE' });

// Avviso turni pubblicati (push + in-app)
export const apiNotifyShifts = (input: {
  from_date: string;
  to_date: string;
}): Promise<NotifyShiftsResult> =>
  request('/shifts/notify', { method: 'POST', body: input });

export const apiMyShiftNotices = (): Promise<ShiftNoticeDto[]> =>
  request('/shifts/my-notices');

export const apiMarkShiftNoticesSeen = (): Promise<void> =>
  request('/shifts/my-notices/seen', { method: 'POST' });

// Preferenze notifiche push del dipendente
export const apiGetNotificationPrefs = (): Promise<NotificationPrefsDto> =>
  request('/me/notification-prefs');

export const apiUpdateNotificationPrefs = (
  input: Partial<NotificationPrefsDto>,
): Promise<NotificationPrefsDto> =>
  request('/me/notification-prefs', { method: 'PATCH', body: input });

// ---------------------------------------------------------------------------
// Shift services — configurazione servizi giornalieri della location
// ---------------------------------------------------------------------------

export const apiListShiftServices = (params?: {
  active?: boolean;
}): Promise<ShiftServiceDto[]> =>
  request('/shift-services', { searchParams: params });

export const apiGetShiftService = (id: number): Promise<ShiftServiceDto> =>
  request(`/shift-services/${id}`);

export const apiCreateShiftService = (
  input: ShiftServiceCreateInput,
): Promise<ShiftServiceDto> =>
  request('/shift-services', { method: 'POST', body: input });

export const apiUpdateShiftService = (
  id: number,
  input: ShiftServiceUpdateInput,
): Promise<ShiftServiceDto> =>
  request(`/shift-services/${id}`, { method: 'PATCH', body: input });

export const apiArchiveShiftService = (id: number): Promise<void> =>
  request(`/shift-services/${id}`, { method: 'DELETE' });

/** Elimina DEFINITIVAMENTE un servizio archiviato (409 se ha turni collegati). */
export const apiDeleteShiftServicePermanent = (id: number): Promise<void> =>
  request(`/shift-services/${id}/permanent`, { method: 'DELETE' });

// --- Reparti (macroreparti della location) ---------------------------------

export const apiListDepartments = (params?: {
  include_archived?: boolean;
}): Promise<DepartmentDto[]> =>
  request('/departments', { searchParams: params });

export const apiCreateDepartment = (
  input: DepartmentCreateInput,
): Promise<DepartmentDto> =>
  request('/departments', { method: 'POST', body: input });

export const apiUpdateDepartment = (
  id: number,
  input: DepartmentUpdateInput,
): Promise<DepartmentDto> =>
  request(`/departments/${id}`, { method: 'PATCH', body: input });

export const apiArchiveDepartment = (id: number): Promise<void> =>
  request(`/departments/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Alerts (F&B build #1)
// ---------------------------------------------------------------------------

export const apiListAlerts = (params?: {
  include_resolved?: boolean;
  include_dismissed?: boolean;
  severity?: AlertSeverity;
  type?: AlertType;
  limit?: number;
}): Promise<AlertDto[]> =>
  request('/alerts', { searchParams: params });

export const apiUnreadAlerts = (): Promise<AlertUnreadCountDto> =>
  request('/alerts/unread-count');

export const apiNotificationsSummary = (): Promise<NotificationSummaryDto> =>
  request('/notifications/summary');

// ---------------------------------------------------------------------------
// Eventi locali + flow forecast
// ---------------------------------------------------------------------------

export const apiListLocalEvents = (params?: {
  date_from?: string;
  date_to?: string;
  category?: string;
}): Promise<LocalEventDto[]> =>
  request('/local-events', { searchParams: params });

export const apiCreateLocalEvent = (
  input: LocalEventCreateInput,
): Promise<LocalEventDto> =>
  request('/local-events', { method: 'POST', body: input });

export const apiUpdateLocalEvent = (
  id: number,
  input: LocalEventUpdateInput,
): Promise<LocalEventDto> =>
  request(`/local-events/${id}`, { method: 'PATCH', body: input });

export const apiDeleteLocalEvent = (id: number): Promise<void> =>
  request(`/local-events/${id}`, { method: 'DELETE' });

export const apiGetFlowForecast = (params?: {
  days_ahead?: number;
}): Promise<FlowForecastDto> =>
  request('/forecast/flusso', { searchParams: params });

export const apiSyncWeatherNow = (): Promise<{ created: number }> =>
  request('/local-events/sync-weather', { method: 'POST' });

export const apiSyncTicketmasterNow = (): Promise<{ created: number }> =>
  request('/local-events/sync-ticketmaster', { method: 'POST' });

export const apiDismissAlert = (id: number): Promise<AlertDto> =>
  request(`/alerts/${id}/dismiss`, { method: 'POST' });

export const apiRestoreAlert = (id: number): Promise<AlertDto> =>
  request(`/alerts/${id}/restore`, { method: 'POST' });

// ---------------------------------------------------------------------------
// Recipes + menu engineering (F&B build #3)
// ---------------------------------------------------------------------------

export const apiGetRecipe = (menuItemId: number): Promise<RecipeDto> =>
  request(`/menu-items/${menuItemId}/recipe`);

export const apiUpsertRecipe = (
  menuItemId: number, input: RecipeUpsertInput,
): Promise<RecipeDto> =>
  request(`/menu-items/${menuItemId}/recipe`, { method: 'PUT', body: input });

export const apiDeleteRecipe = (menuItemId: number): Promise<void> =>
  request(`/menu-items/${menuItemId}/recipe`, { method: 'DELETE' });

export const apiGetMenuEngineering = (): Promise<MenuEngineeringSummaryDto> =>
  request('/menu-engineering');

export const apiGetReorderForecast = (params?: {
  days_ahead?: number;
  location_open_on_holidays?: boolean;
}): Promise<ForecastSummaryDto> =>
  request('/forecast/reorder', { searchParams: params });

// Sourcing (F&B build #5) — confronto fornitori prezzi storici

export const apiGetSupplierComparison = (): Promise<SourcingSummaryDto> =>
  request('/sourcing/comparison');

export const apiGetNegotiationEmail = (params: {
  product_id: number;
  supplier_id: number;
}): Promise<NegotiationEmailDto> =>
  request('/sourcing/negotiation-email', { searchParams: params });

export const apiGetMonthlyPayroll = (
  year: number,
  month: number,
): Promise<MonthlyPayrollSummaryDto> =>
  request('/payroll/monthly', { searchParams: { year, month } });

export const apiGetPayrollSummary = (params: {
  period: PayrollPeriod;
  start: string; // YYYY-MM-DD
}): Promise<PayrollPeriodSummaryDto> =>
  request('/payroll/summary', { searchParams: params });

/**
 * Scarica il PDF del report mensile (F&B build #2). Forza il download
 * con un <a download> programmatico cosi' il proprietario riceve il
 * file direttamente.
 */
/** Resoconto presenze mensile per il consulente del lavoro (PDF o CSV). */
// ---------------------------------------------------------------------------
// Scadenzario pagamenti
// ---------------------------------------------------------------------------

export const apiGetScadenzario = (): Promise<PaymentsScheduleDto> =>
  request('/payments/scadenzario');

export const apiUpdateInvoicePayment = (
  id: number,
  input: {
    payment_method?: PaymentMethod | null;
    payment_bank?: string | null;
    due_date?: string | null;
    paid_at?: string | null;
  },
): Promise<InvoiceDetailDto> =>
  request(`/invoices/${id}/payment`, { method: 'PATCH', body: input });

/** Banche già usate nei pagamenti della location (datalist UI). */
export const apiGetPaymentBanks = (): Promise<string[]> =>
  request('/payments/banks');

/** Export CSV dei pagamenti effettuati nel mese (per il commercialista). */
export async function downloadPaymentsCsv(
  year: number,
  month: number,
): Promise<void> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const url = `${API_BASE_URL}/payments/report.csv?year=${year}&month=${month}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let detail = `Export non disponibile (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* niente */
    }
    throw new ApiError(res.status, detail);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `pagamenti_${year}_${String(month).padStart(2, '0')}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
}

// ---------------------------------------------------------------------------
// Error tracking interno
// ---------------------------------------------------------------------------

export interface ErrorEventDto {
  id: number;
  fingerprint: string;
  source: 'backend' | 'frontend';
  kind: string;
  message: string;
  stack: string | null;
  context: string | null;
  user_agent: string | null;
  count: number;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
}

export const apiReportError = (input: {
  message: string;
  stack?: string | null;
  url?: string | null;
}): Promise<{ ok: boolean }> =>
  request('/errors/report', { method: 'POST', body: input });

export const apiListErrors = (): Promise<ErrorEventDto[]> =>
  request('/errors');

export const apiResolveError = (id: number): Promise<{ ok: boolean }> =>
  request(`/errors/${id}/resolve`, { method: 'PATCH' });

// ---------------------------------------------------------------------------
// Web Push
// ---------------------------------------------------------------------------

export const apiGetVapidKey = (): Promise<{
  enabled: boolean;
  public_key: string | null;
}> => request('/push/vapid-public-key');

export const apiPushSubscribe = (sub: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}): Promise<{ ok: boolean }> =>
  request('/push/subscribe', { method: 'POST', body: sub });

export const apiPushUnsubscribe = (endpoint: string): Promise<{ ok: boolean }> =>
  request('/push/unsubscribe', { method: 'POST', body: { endpoint } });

export const apiPushTest = (): Promise<{ sent: number }> =>
  request('/push/test', { method: 'POST' });

// ---------------------------------------------------------------------------
// Rateizzazioni AdE
// ---------------------------------------------------------------------------

export const apiParseTaxPlanPdf = (file: File): Promise<ParsedTaxPlanDto> => {
  const fd = new FormData();
  fd.append('file', file);
  return request('/tax-plans/parse-document', { method: 'POST', body: fd });
};

export const apiCreateTaxPlan = (input: {
  raw_pdf_b64?: string | null;
  raw_pdf_name?: string | null;
  creditor: string;
  reference: string;
  taxpayer_code?: string | null;
  granted_at?: string | null;
  description?: string | null;
  installments: { number: number; due_date: string; amount: string }[];
  mark_past_as_paid: boolean;
}): Promise<TaxPlanDto> =>
  request('/tax-plans', { method: 'POST', body: input });

export const apiListTaxPlans = (): Promise<TaxPlanDto[]> =>
  request('/tax-plans');

export const apiUpdateTaxInstallment = (
  id: number,
  input: { paid_at?: string | null; payment_bank?: string | null },
): Promise<TaxInstallmentDto> =>
  request(`/tax-plans/installments/${id}`, {
    method: 'PATCH', body: input,
  });

export const apiDeleteTaxPlan = (id: number): Promise<void> =>
  request(`/tax-plans/${id}`, { method: 'DELETE' });

export async function downloadConsultantReport(
  year: number,
  month: number,
  fmt: 'pdf' | 'csv',
): Promise<void> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const url = `${API_BASE_URL}/payroll/consultant-report.${fmt}?year=${year}&month=${month}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let detail = `Resoconto non disponibile (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* niente */
    }
    throw new ApiError(res.status, detail);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `presenze_${year}_${String(month).padStart(2, '0')}.${fmt}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
}

export async function downloadMonthlyReportPdf(
  year: number,
  month: number,
): Promise<void> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = { Accept: 'application/pdf' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const url = `${API_BASE_URL}/reports/monthly?year=${year}&month=${month}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let detail = `Report non disponibile (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* niente */
    }
    throw new ApiError(res.status, detail);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `manteco-report-${year}-${String(month).padStart(2, '0')}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

/** Carica un CSV su un endpoint .../import.csv e ritorna l'ImportReport. */
export async function uploadCsv(
  path: string,
  file: File,
): Promise<CsvImportReport> {
  const fd = new FormData();
  fd.append('file', file);
  return request<CsvImportReport>(path, { method: 'POST', body: fd });
}

/** Scarica un PDF dall'API e forza il download via blob. */
export async function downloadPdf(
  path: string,
  filenameFallback: string,
): Promise<void> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = { Accept: 'application/pdf' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!res.ok) {
    let detail = `Download non riuscito (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* niente */
    }
    throw new ApiError(res.status, detail);
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = cd.match(/filename="?([^";]+)"?/);
  const fname = m?.[1] ?? filenameFallback;
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

/** Scarica una risorsa CSV dall'API e forza il download via blob.
 *  Usa l'auth bearer dal token storage. `path` includere querystring. */
export async function downloadCsv(
  path: string,
  filenameFallback: string,
): Promise<void> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = { Accept: 'text/csv' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!res.ok) {
    let detail = `Esportazione non riuscita (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* niente */
    }
    throw new ApiError(res.status, detail);
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  // estraggo filename="..." se presente
  const m = cd.match(/filename="?([^";]+)"?/);
  const fname = m?.[1] ?? filenameFallback;
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

export const apiListAdvances = (params?: {
  from_date?: string;
  to_date?: string;
  employee_id?: number;
}): Promise<EmployeeAdvanceDto[]> =>
  request('/advances', { searchParams: params });

export const apiGetAdvance = (id: number): Promise<EmployeeAdvanceDto> =>
  request(`/advances/${id}`);

export const apiCreateAdvance = (
  input: EmployeeAdvanceCreateInput,
): Promise<EmployeeAdvanceDto> =>
  request('/advances', { method: 'POST', body: input });

export const apiUpdateAdvance = (
  id: number,
  input: EmployeeAdvanceUpdateInput,
): Promise<EmployeeAdvanceDto> =>
  request(`/advances/${id}`, { method: 'PATCH', body: input });

export const apiDeleteAdvance = (id: number): Promise<void> =>
  request(`/advances/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const apiUpdateMe = (input: UpdateProfileInput): Promise<UserDto> =>
  request('/auth/me', { method: 'PATCH', body: input });

export const apiChangePassword = (input: ChangePasswordInput): Promise<void> =>
  request('/auth/me/password', { method: 'POST', body: input });

export const apiGetCurrentCompany = (): Promise<CompanyMiniDto> =>
  request('/companies/current');

export const apiUpdateCurrentCompany = (
  input: UpdateCompanyInput,
): Promise<CompanyMiniDto> =>
  request('/companies/current', { method: 'PATCH', body: input });

export const apiCompleteOnboarding = (): Promise<CompanyMiniDto> =>
  request('/companies/current/onboarding-complete', { method: 'POST' });

export const apiListLocations = (): Promise<LocationFullDto[]> =>
  request('/locations');

export const apiCreateLocation = (
  input: LocationCreateInput,
): Promise<LocationFullDto> =>
  request('/locations', { method: 'POST', body: input });

export const apiUpdateLocation = (
  id: number,
  input: LocationUpdateInput,
): Promise<LocationFullDto> =>
  request(`/locations/${id}`, { method: 'PATCH', body: input });

export const apiDeleteLocation = (id: number): Promise<void> =>
  request(`/locations/${id}`, { method: 'DELETE' });

/** Carica/sostituisce il logo del locale (owner). PNG/JPEG/WebP/SVG, max 2 MB. */
export const apiUploadLocationLogo = (
  id: number,
  file: File,
): Promise<LocationFullDto> => {
  const fd = new FormData();
  fd.append('file', file);
  return request(`/locations/${id}/logo`, { method: 'PUT', body: fd });
};

export const apiDeleteLocationLogo = (id: number): Promise<void> =>
  request(`/locations/${id}/logo`, { method: 'DELETE' });

/** Scarica il logo come Blob (endpoint autenticato → niente <img src> diretto).
 *  Ritorna null se il locale non ha logo (404). */
export async function apiFetchLocationLogo(id: number): Promise<Blob | null> {
  const token = authStorage.getToken();
  const res = await fetch(`${API_BASE_URL}/locations/${id}/logo`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new ApiError(res.status, `Logo non disponibile (${res.status})`);
  return res.blob();
}

export const apiListMembers = (): Promise<MemberDto[]> => request('/memberships');

export const apiAddMember = (input: AddMemberInput): Promise<MemberDto> =>
  request('/memberships', { method: 'POST', body: input });

export const apiUpdateMember = (
  id: number,
  input: UpdateMembershipInput,
): Promise<MemberDto> =>
  request(`/memberships/${id}`, { method: 'PATCH', body: input });

export const apiArchiveMember = (id: number): Promise<MemberDto> =>
  request(`/memberships/${id}/archive`, { method: 'POST' });

export const apiResendMemberInvite = (id: number): Promise<MemberDto> =>
  request(`/memberships/${id}/resend-invite`, { method: 'POST' });

export const apiValidateMembershipInvite = (
  token: string,
): Promise<MembershipInvitePublicDto> =>
  request(`/auth/membership-invite/${encodeURIComponent(token)}`, { silent401: true });

export const apiAcceptMembershipInvite = (
  input: AcceptMembershipInviteInput,
): Promise<AuthSuccessDto> =>
  request('/auth/accept-membership-invite', { method: 'POST', body: input, silent401: true });

export const apiRestoreMember = (id: number): Promise<MemberDto> =>
  request(`/memberships/${id}/restore`, { method: 'POST' });

export const apiRemoveMember = (id: number): Promise<void> =>
  request(`/memberships/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Documenti del dipendente (buste paga, CUD, contratti)
// ---------------------------------------------------------------------------

export const apiListEmployeeDocuments = (
  employeeId: number,
): Promise<EmployeeDocumentDto[]> =>
  request(`/employees/${employeeId}/documents`);

export const apiUploadEmployeeDocument = (
  employeeId: number,
  input: { file: File; category: DocumentCategory; title: string; period?: string; notes?: string },
): Promise<EmployeeDocumentDto> => {
  const fd = new FormData();
  fd.append('file', input.file);
  fd.append('category', input.category);
  fd.append('title', input.title);
  if (input.period) fd.append('period', input.period);
  if (input.notes) fd.append('notes', input.notes);
  return request(`/employees/${employeeId}/documents`, { method: 'POST', body: fd });
};

export const apiDeleteEmployeeDocument = (docId: number): Promise<void> =>
  request(`/employee-documents/${docId}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Bacheca Lavoro (talent pool)
// ---------------------------------------------------------------------------

export const apiListTalent = (role?: string): Promise<TalentCandidateDto[]> =>
  request(`/talent${role && role.trim() ? `?role=${encodeURIComponent(role.trim())}` : ''}`);

export const apiGetTalentCandidate = (id: number): Promise<TalentCandidateDto> =>
  request(`/talent/${id}`);

export const apiGetMyTalent = (): Promise<TalentProfileMeDto> =>
  request('/talent/me');

export const apiUpdateMyTalent = (
  input: TalentProfileUpdateInput,
): Promise<TalentProfileMeDto> =>
  request('/talent/me', { method: 'PATCH', body: input });

export const apiAddDeclaredExperience = (
  input: DeclaredExperienceInput,
): Promise<TalentProfileMeDto> =>
  request('/talent/me/experiences', { method: 'POST', body: input });

export const apiUpdateDeclaredExperience = (
  id: number,
  input: DeclaredExperienceInput,
): Promise<TalentProfileMeDto> =>
  request(`/talent/me/experiences/${id}`, { method: 'PATCH', body: input });

export const apiDeleteDeclaredExperience = (
  id: number,
): Promise<TalentProfileMeDto> =>
  request(`/talent/me/experiences/${id}`, { method: 'DELETE' });

// Referenze portabili — lato lavoratore
export const apiRequestReference = (
  sourceExperienceId: number,
): Promise<TalentProfileMeDto> =>
  request('/talent/me/references', {
    method: 'POST',
    body: { source_experience_id: sourceExperienceId },
  });

export const apiSetReferenceAttached = (
  refId: number,
  attached: boolean,
): Promise<TalentProfileMeDto> =>
  request(`/talent/me/references/${refId}`, { method: 'PATCH', body: { attached } });

// Referenze portabili — lato ex datore
export const apiListReferenceRequests = (): Promise<ReferenceRequestDto[]> =>
  request('/talent/reference-requests');

export const apiGrantReference = (
  refId: number,
  input: ReferenceGrantInput,
): Promise<ReferenceRequestDto> =>
  request(`/talent/reference-requests/${refId}/grant`, { method: 'POST', body: input });

export const apiDeclineReference = (refId: number): Promise<ReferenceRequestDto> =>
  request(`/talent/reference-requests/${refId}/decline`, { method: 'POST' });

export const apiRevokeReference = (refId: number): Promise<ReferenceRequestDto> =>
  request(`/talent/reference-requests/${refId}/revoke`, { method: 'POST' });

// Posizioni vacanti — catalogo + preferenze
export const apiListTalentRoles = (): Promise<TalentRoleDto[]> => request('/talent/roles');

export const apiUpdateJobPreferences = (
  input: JobPreferencesInput,
): Promise<TalentProfileMeDto> =>
  request('/talent/me/job-preferences', { method: 'PATCH', body: input });

// Posizioni vacanti — azienda
export const apiCreateOpening = (input: OpeningCreateInput): Promise<JobOpeningMineDto> =>
  request('/talent/openings', { method: 'POST', body: input });

export const apiListMyOpenings = (): Promise<JobOpeningMineDto[]> =>
  request('/talent/openings/mine');

export const apiCloseOpening = (id: number): Promise<JobOpeningMineDto> =>
  request(`/talent/openings/${id}/close`, { method: 'POST' });

export const apiReopenOpening = (id: number): Promise<JobOpeningMineDto> =>
  request(`/talent/openings/${id}/reopen`, { method: 'POST' });

export const apiDeleteOpening = (id: number): Promise<{ ok: boolean }> =>
  request(`/talent/openings/${id}`, { method: 'DELETE' });

export const apiListOpeningApplications = (id: number): Promise<ApplicationDto[]> =>
  request(`/talent/openings/${id}/applications`);

export const apiSetApplicationStatus = (
  appId: number,
  statusValue: 'shortlisted' | 'rejected' | 'received',
): Promise<ApplicationDto> =>
  request(`/talent/applications/${appId}`, { method: 'PATCH', body: { status: statusValue } });

// Posizioni vacanti — lavoratore
export const apiOpeningsFeed = (mine = true): Promise<JobOpeningFeedDto[]> =>
  request(`/talent/openings${mine ? '' : '?mine=false'}`);

export const apiApplyToOpening = (
  openingId: number,
  role: string,
  note?: string | null,
): Promise<MyApplicationDto> =>
  request(`/talent/openings/${openingId}/apply`, { method: 'POST', body: { role, note: note ?? null } });

export const apiMyApplications = (): Promise<MyApplicationDto[]> =>
  request('/talent/me/applications');

export const apiWithdrawApplication = (appId: number): Promise<{ ok: boolean }> =>
  request(`/talent/me/applications/${appId}`, { method: 'DELETE' });

export const apiNetworkSummary = (): Promise<NetworkSummaryDto> =>
  request('/talent/network-summary');

// Record interni del team
export const apiTeamRecords = (): Promise<TeamRecordDto[]> =>
  request('/team-records');

export const apiEmployeeRecord = (employeeId: number): Promise<TeamRecordDetailDto> =>
  request(`/team-records/${employeeId}`);

export const apiAddLateness = (
  employeeId: number,
  input: LatenessInput,
): Promise<LatenessDto> =>
  request(`/team-records/${employeeId}/lateness`, { method: 'POST', body: input });

export const apiDeleteLateness = (latenessId: number): Promise<{ ok: boolean }> =>
  request(`/team-records/lateness/${latenessId}`, { method: 'DELETE' });

export const apiAddEmployeeNote = (
  employeeId: number,
  input: NoteInput,
): Promise<EmployeeNoteDto> =>
  request(`/team-records/${employeeId}/notes`, { method: 'POST', body: input });

export const apiDeleteEmployeeNote = (noteId: number): Promise<{ ok: boolean }> =>
  request(`/team-records/notes/${noteId}`, { method: 'DELETE' });

// Timbratura
export const apiTimeClockConfig = (): Promise<TimeClockConfigDto> =>
  request('/timeclock/config');

export const apiTimeClockMe = (): Promise<MeTimeClockDto> =>
  request('/timeclock/me');

export const apiPunch = (input: PunchInput): Promise<PunchResultDto> =>
  request('/timeclock/punch', { method: 'POST', body: input });

export const apiTimeClockToday = (): Promise<TodayRowDto[]> =>
  request('/timeclock/today');

export const apiTimeClockSettings = (): Promise<TimeClockSettingsDto> =>
  request('/timeclock/settings');

export const apiUpdateTimeClockSettings = (
  input: TimeClockSettingsInput,
): Promise<TimeClockSettingsDto> =>
  request('/timeclock/settings', { method: 'PATCH', body: input });

export const apiRegenerateTimeClockQr = (): Promise<TimeClockSettingsDto> =>
  request('/timeclock/settings/regenerate-qr', { method: 'POST' });

export const apiTimeClockHours = (year: number, month: number): Promise<HoursRowDto[]> =>
  request(`/timeclock/hours?year=${year}&month=${month}`);

/** Scarica il file con il token in header (no link diretto: serve auth). */
export async function apiDownloadEmployeeDocument(
  docId: number,
  filename: string,
): Promise<void> {
  const token = authStorage.getToken();
  const res = await fetch(`${API_BASE_URL}/employee-documents/${docId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, `Download non riuscito (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Invoices (FatturaPA)
// ---------------------------------------------------------------------------

export const apiListInvoices = (params?: {
  status?: InvoiceStatus;
  supplier_id?: number;
  from_date?: string;
  to_date?: string;
}): Promise<InvoiceListDto[]> =>
  request('/invoices', { searchParams: params });

export const apiGetInvoice = (id: number): Promise<InvoiceDetailDto> =>
  request(`/invoices/${id}`);

export const apiUploadInvoiceXml = async (
  file: File,
): Promise<InvoiceDetailDto> => {
  const form = new FormData();
  form.append('file', file);
  return request('/invoices/upload-xml', { method: 'POST', body: form });
};

export const apiUploadInvoiceDocument = async (
  file: File,
  documentType: 'fattura' | 'ddt' | 'scontrino',
): Promise<InvoiceDetailDto> => {
  const form = new FormData();
  form.append('file', file);
  form.append('document_type', documentType);
  return request('/invoices/upload-document', { method: 'POST', body: form });
};

export const apiConfirmInvoice = (
  id: number,
  input: InvoiceConfirmInput,
): Promise<InvoiceConfirmResult> =>
  request(`/invoices/${id}/confirm`, { method: 'POST', body: input });

export const apiDeleteInvoice = (id: number): Promise<void> =>
  request(`/invoices/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export const apiListContracts = (params?: {
  category?: ContractCategory;
  active?: boolean;
}): Promise<ContractDto[]> =>
  request('/contracts', { searchParams: params });

export const apiGetContract = (id: number): Promise<ContractDto> =>
  request(`/contracts/${id}`);

export const apiCreateContract = (input: ContractCreateInput): Promise<ContractDto> =>
  request('/contracts', { method: 'POST', body: input });

export const apiUpdateContract = (
  id: number,
  input: ContractUpdateInput,
): Promise<ContractDto> =>
  request(`/contracts/${id}`, { method: 'PATCH', body: input });

export const apiDeleteContract = (id: number): Promise<void> =>
  request(`/contracts/${id}`, { method: 'DELETE' });

export const apiGetUpcomingContracts = (
  windowDays: number = 90,
): Promise<ContractsSummaryDto> =>
  request('/contracts/upcoming', { searchParams: { window_days: windowDays } });

// ---------------------------------------------------------------------------
// HACCP
// ---------------------------------------------------------------------------

export const apiListEquipment = (params?: {
  type?: EquipmentType;
  active?: boolean;
}): Promise<EquipmentDto[]> =>
  request('/haccp/equipment', { searchParams: params });

export const apiGetEquipment = (id: number): Promise<EquipmentDto> =>
  request(`/haccp/equipment/${id}`);

export const apiCreateEquipment = (input: EquipmentCreateInput): Promise<EquipmentDto> =>
  request('/haccp/equipment', { method: 'POST', body: input });

export const apiUpdateEquipment = (
  id: number,
  input: EquipmentUpdateInput,
): Promise<EquipmentDto> =>
  request(`/haccp/equipment/${id}`, { method: 'PATCH', body: input });

export const apiDeleteEquipment = (id: number): Promise<void> =>
  request(`/haccp/equipment/${id}`, { method: 'DELETE' });

export const apiListTemperatureReadings = (params?: {
  equipment_id?: number;
  from_date?: string;
  to_date?: string;
}): Promise<TemperatureReadingDto[]> =>
  request('/haccp/readings', { searchParams: params });

export const apiCreateTemperatureReading = (
  input: TemperatureReadingCreateInput,
): Promise<TemperatureReadingDto> =>
  request('/haccp/readings', { method: 'POST', body: input });

export const apiDeleteTemperatureReading = (id: number): Promise<void> =>
  request(`/haccp/readings/${id}`, { method: 'DELETE' });

export const apiGetHaccpDashboard = (date?: string): Promise<HaccpDashboardDto> =>
  request('/haccp/dashboard', {
    searchParams: date ? { date } : undefined,
  });

// ---------------------------------------------------------------------------
// Calendario chiusure locale
// ---------------------------------------------------------------------------

export const apiListClosures = (): Promise<LocationClosureDto[]> =>
  request('/closures');

export const apiCreateClosure = (
  input: LocationClosureCreateInput,
): Promise<LocationClosureDto> =>
  request('/closures', { method: 'POST', body: input });

export const apiDeleteClosure = (id: number): Promise<void> =>
  request(`/closures/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Sensori wireless HACCP
// ---------------------------------------------------------------------------

export const apiListSensorChannels = (): Promise<SensorChannelDto[]> =>
  request('/haccp/sensors');

export const apiCreateSensorChannel = (
  input: SensorChannelCreateInput,
): Promise<SensorChannelDto> =>
  request('/haccp/sensors', { method: 'POST', body: input });

export const apiUpdateSensorChannel = (
  id: number, input: SensorChannelUpdateInput,
): Promise<SensorChannelDto> =>
  request(`/haccp/sensors/${id}`, { method: 'PATCH', body: input });

export const apiDeleteSensorChannel = (id: number): Promise<void> =>
  request(`/haccp/sensors/${id}`, { method: 'DELETE' });

export const apiPollSensorChannel = (id: number): Promise<SensorChannelDto> =>
  request(`/haccp/sensors/${id}/poll`, { method: 'POST' });

export const apiSensorChannelReadings = (
  id: number, hours = 24,
): Promise<SensorRawReadingDto[]> =>
  request(`/haccp/sensors/${id}/readings`, {
    searchParams: { hours: String(hours) },
  });

// ---------------------------------------------------------------------------
// Abbattimento
// ---------------------------------------------------------------------------

export const apiListBlastChillCycles = (params?: {
  open_only?: boolean;
}): Promise<BlastChillCycleDto[]> =>
  request('/haccp/blast-chill/cycles', {
    searchParams: params?.open_only ? { open_only: 'true' } : undefined,
  });

export const apiStartBlastChill = (
  input: BlastChillStartInput,
): Promise<BlastChillCycleDto> =>
  request('/haccp/blast-chill/cycles', { method: 'POST', body: input });

export const apiCompleteBlastChill = (
  id: number, input: BlastChillCompleteInput,
): Promise<BlastChillCycleDto> =>
  request(`/haccp/blast-chill/cycles/${id}/complete`, {
    method: 'POST', body: input,
  });

export const apiAbortBlastChill = (id: number): Promise<BlastChillCycleDto> =>
  request(`/haccp/blast-chill/cycles/${id}/abort`, { method: 'POST' });

/** Apre l'etichetta abbattimento (fetch+blob per la Bearer auth). */
export async function openBlastChillLabelPdf(
  id: number, copies = 1,
): Promise<void> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(
    `${API_BASE_URL}/haccp/blast-chill/cycles/${id}/label.pdf?copies=${copies}`,
    { headers },
  );
  if (!res.ok) {
    throw new ApiError(res.status, `Etichetta non disponibile (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  if (!opened) {
    const a = document.createElement('a');
    a.href = url;
    a.download = `abbattimento-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

// ---------------------------------------------------------------------------
// HACCP certificates personale
// ---------------------------------------------------------------------------

export const apiListHaccpCertificates = (params?: {
  employee_id?: number;
}): Promise<HaccpCertificateDto[]> =>
  request('/haccp/certificates', { searchParams: params });

export const apiCreateHaccpCertificate = (
  input: HaccpCertificateCreateInput,
): Promise<HaccpCertificateDto> =>
  request('/haccp/certificates', { method: 'POST', body: input });

export const apiUpdateHaccpCertificate = (
  id: number,
  input: HaccpCertificateUpdateInput,
): Promise<HaccpCertificateDto> =>
  request(`/haccp/certificates/${id}`, { method: 'PATCH', body: input });

export const apiDeleteHaccpCertificate = (id: number): Promise<void> =>
  request(`/haccp/certificates/${id}`, { method: 'DELETE' });

export const apiGetHaccpCertificatesDashboard = (): Promise<HaccpCertificatesDashboard> =>
  request('/haccp/certificates/dashboard');

// ---------------------------------------------------------------------------
// HACCP Cleaning — Pilastro 5.2.3
// ---------------------------------------------------------------------------

export const apiGetCleaningDashboard = (): Promise<CleaningDashboard> =>
  request('/haccp/cleaning/dashboard');

export const apiListCleaningTasks = (params?: {
  active?: boolean;
  area?: CleaningArea;
}): Promise<CleaningTaskDto[]> => {
  const qs = new URLSearchParams();
  if (params?.active !== undefined) qs.set('active', String(params.active));
  if (params?.area) qs.set('area', params.area);
  const q = qs.toString();
  return request(`/haccp/cleaning/tasks${q ? `?${q}` : ''}`);
};

export const apiCreateCleaningTask = (
  input: CleaningTaskCreateInput,
): Promise<CleaningTaskDto> =>
  request('/haccp/cleaning/tasks', { method: 'POST', body: input });

export const apiUpdateCleaningTask = (
  id: number,
  input: CleaningTaskUpdateInput,
): Promise<CleaningTaskDto> =>
  request(`/haccp/cleaning/tasks/${id}`, { method: 'PATCH', body: input });

export const apiArchiveCleaningTask = (id: number): Promise<void> =>
  request(`/haccp/cleaning/tasks/${id}`, { method: 'DELETE' });

export const apiListCleaningLogs = (params?: {
  task_id?: number;
  days?: number;
}): Promise<CleaningLogDto[]> => {
  const qs = new URLSearchParams();
  if (params?.task_id !== undefined) qs.set('task_id', String(params.task_id));
  if (params?.days !== undefined) qs.set('days', String(params.days));
  const q = qs.toString();
  return request(`/haccp/cleaning/logs${q ? `?${q}` : ''}`);
};

export const apiCreateCleaningLog = (
  input: CleaningLogCreateInput,
): Promise<CleaningLogDto> =>
  request('/haccp/cleaning/logs', { method: 'POST', body: input });

export const apiDeleteCleaningLog = (id: number): Promise<void> =>
  request(`/haccp/cleaning/logs/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Audit log (Pilastro 5.5)
// ---------------------------------------------------------------------------

export const apiListAuditLogs = (params?: {
  date_from?: string;
  date_to?: string;
  entity_type?: string;
  action?: string;
  actor_user_id?: number;
  limit?: number;
}): Promise<AuditLogDto[]> => {
  const qs = new URLSearchParams();
  if (params?.date_from) qs.set('date_from', params.date_from);
  if (params?.date_to) qs.set('date_to', params.date_to);
  if (params?.entity_type) qs.set('entity_type', params.entity_type);
  if (params?.action) qs.set('action', params.action);
  if (params?.actor_user_id) qs.set('actor_user_id', String(params.actor_user_id));
  if (params?.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return request(`/audit/logs${q ? `?${q}` : ''}`);
};

export const apiVerifyAuditChain = (params?: {
  date_from?: string;
  date_to?: string;
}): Promise<AuditChainVerificationDto> => {
  const qs = new URLSearchParams();
  if (params?.date_from) qs.set('date_from', params.date_from);
  if (params?.date_to) qs.set('date_to', params.date_to);
  const q = qs.toString();
  return request(`/audit/verify${q ? `?${q}` : ''}`);
};

// ---------------------------------------------------------------------------
// Payroll simulator (Pilastro 3)
// ---------------------------------------------------------------------------

export const apiGetPayrollSimulatorMeta = (): Promise<PayrollSimulatorMeta> =>
  request('/payroll/simulator/meta');

export const apiRunPayrollSimulation = (
  input: PayrollSimulationRequest,
): Promise<PayrollSimulationResponse> =>
  request('/payroll/simulator', { method: 'POST', body: input });

// ---------------------------------------------------------------------------
// Transfers inter-location (Pilastro 1.1)
// ---------------------------------------------------------------------------

export const apiCreateTransfer = (
  input: TransferCreateInput,
): Promise<TransferDto> =>
  request('/transfers', { method: 'POST', body: input });

export const apiListTransfers = (params?: {
  direction?: 'in' | 'out';
  date_from?: string;
  date_to?: string;
  product_id?: number;
}): Promise<TransferDto[]> =>
  request('/transfers', { searchParams: params });

export const apiGetTransfer = (movementId: number): Promise<TransferDto> =>
  request(`/transfers/${movementId}`);

// ---------------------------------------------------------------------------
// Multi-location dashboard (Pilastro 1.2)
// ---------------------------------------------------------------------------

export const apiGetMultiLocationDashboard = (params: {
  year: number;
  month?: number;
}): Promise<MultiLocationDashboardDto> => {
  const qs = new URLSearchParams();
  qs.set('year', String(params.year));
  if (params.month !== undefined) qs.set('month', String(params.month));
  return request(`/multi-location/dashboard?${qs.toString()}`);
};

// ---------------------------------------------------------------------------
// Holding (raggruppamento Company + consolidato)
// ---------------------------------------------------------------------------

export const apiMyHoldings = (): Promise<HoldingDto[]> =>
  request('/holdings/me');

export const apiCreateHolding = (input: {
  name: string;
  vat_number?: string | null;
}): Promise<HoldingDto> =>
  request('/holdings', { method: 'POST', body: input });

export const apiCreateCompanyInHolding = (
  holdingId: number,
  input: { name: string; slug?: string; location_name: string },
): Promise<CompanyInHoldingDto> =>
  request(`/holdings/${holdingId}/companies`, { method: 'POST', body: input });

export const apiAttachCompany = (
  holdingId: number,
  companyId: number,
): Promise<HoldingDto> =>
  request(`/holdings/${holdingId}/attach`, {
    method: 'POST',
    body: { company_id: companyId },
  });

export const apiDetachCompany = (
  holdingId: number,
  companyId: number,
): Promise<HoldingDto> =>
  request(`/holdings/${holdingId}/detach`, {
    method: 'POST',
    body: { company_id: companyId },
  });

export const apiGetHoldingDashboard = (
  holdingId: number,
  params: { year: number; month?: number },
): Promise<HoldingDashboardDto> => {
  const qs = new URLSearchParams();
  qs.set('year', String(params.year));
  if (params.month !== undefined) qs.set('month', String(params.month));
  return request(`/holdings/${holdingId}/dashboard?${qs.toString()}`);
};

// ---------------------------------------------------------------------------
// Menu items + Sales analytics (Pilastro 4.1)
// ---------------------------------------------------------------------------

export const apiListMenuItems = (params?: {
  active?: boolean;
  category?: string;
  search?: string;
}): Promise<MenuItemDto[]> => {
  const qs = new URLSearchParams();
  if (params?.active !== undefined) qs.set('active', String(params.active));
  if (params?.category) qs.set('category', params.category);
  if (params?.search) qs.set('search', params.search);
  const q = qs.toString();
  return request(`/menu-items${q ? `?${q}` : ''}`);
};

export const apiCreateMenuItem = (input: MenuItemCreateInput): Promise<MenuItemDto> =>
  request('/menu-items', { method: 'POST', body: input });

export const apiUpdateMenuItem = (
  id: number, input: MenuItemUpdateInput,
): Promise<MenuItemDto> =>
  request(`/menu-items/${id}`, { method: 'PATCH', body: input });

export const apiArchiveMenuItem = (id: number): Promise<void> =>
  request(`/menu-items/${id}`, { method: 'DELETE' });

export const apiBulkArchiveMenuItems = (
  ids: number[],
): Promise<{ archived: number }> =>
  request('/menu-items/bulk-archive', { method: 'POST', body: { ids } });

export const apiBulkRecordSales = (
  input: MenuItemSalesBulkInput,
): Promise<MenuItemSaleDto[]> =>
  request('/menu-item-sales/bulk', { method: 'POST', body: input });

/**
 * Upload CSV export from any POS to the /menu-item-sales/import-csv endpoint.
 * Uses multipart/form-data — bypasses the JSON `request()` helper.
 */
export async function apiImportSalesCsv(file: File): Promise<CsvImportResult> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE_URL}/menu-item-sales/import-csv`, {
    method: 'POST',
    headers,
    body: formData,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = data?.detail ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, typeof detail === 'string' ? detail : JSON.stringify(detail), data);
  }
  return data as CsvImportResult;
}

export const apiListMenuItemSales = (params?: {
  date_from?: string;
  date_to?: string;
  menu_item_id?: number;
}): Promise<MenuItemSaleDto[]> => {
  const qs = new URLSearchParams();
  if (params?.date_from) qs.set('date_from', params.date_from);
  if (params?.date_to) qs.set('date_to', params.date_to);
  if (params?.menu_item_id) qs.set('menu_item_id', String(params.menu_item_id));
  const q = qs.toString();
  return request(`/menu-item-sales${q ? `?${q}` : ''}`);
};

export const apiGetSalesAnalytics = (params: {
  year: number;
  month: number;
  top_limit?: number;
  worst_limit?: number;
}): Promise<SalesAnalyticsDashboardDto> => {
  const qs = new URLSearchParams();
  qs.set('year', String(params.year));
  qs.set('month', String(params.month));
  if (params.top_limit) qs.set('top_limit', String(params.top_limit));
  if (params.worst_limit) qs.set('worst_limit', String(params.worst_limit));
  return request(`/analytics/sales/dashboard?${qs.toString()}`);
};

export const apiGetCostDiagnostics = (params: {
  year: number;
  month: number;
}): Promise<CostDiagnosticsDashboardDto> => {
  const qs = new URLSearchParams();
  qs.set('year', String(params.year));
  qs.set('month', String(params.month));
  return request(`/analytics/costs/dashboard?${qs.toString()}`);
};

// ---------------------------------------------------------------------------
// POS providers (Pilastro 4.4)
// ---------------------------------------------------------------------------

export const apiListPosProviders = (): Promise<PosProviderDto[]> =>
  request('/pos/providers');

export const apiListPosCredentials = (): Promise<PosCredentialsDto[]> =>
  request('/pos/credentials');

export const apiCreatePosCredentials = (
  input: PosCredentialsCreateInput,
): Promise<PosCredentialsDto> =>
  request('/pos/credentials', { method: 'POST', body: input });

export const apiUpdatePosCredentials = (
  id: number, input: PosCredentialsUpdateInput,
): Promise<PosCredentialsDto> =>
  request(`/pos/credentials/${id}`, { method: 'PATCH', body: input });

export const apiDeletePosCredentials = (id: number): Promise<void> =>
  request(`/pos/credentials/${id}`, { method: 'DELETE' });

export const apiTriggerPosSync = (id: number): Promise<PosSyncResultDto> =>
  request(`/pos/credentials/${id}/sync`, { method: 'POST' });

// ---------------------------------------------------------------------------
// POS closure imports — chiusure via email (Pilastro 4.5, canale email)
// ---------------------------------------------------------------------------

export const apiListClosureImports = (): Promise<PosClosureImportDto[]> =>
  request('/pos/closure-imports');

export const apiGetClosureImport = (
  id: number,
): Promise<PosClosureImportDetailDto> =>
  request(`/pos/closure-imports/${id}`);

export const apiReprocessClosureImport = (
  id: number,
): Promise<PosClosureImportDetailDto> =>
  request(`/pos/closure-imports/${id}/reprocess`, { method: 'POST' });

export const apiPollClosureMailbox = (): Promise<PosEmailPollResultDto> =>
  request('/pos/closure-imports/poll', { method: 'POST' });

export const apiCreateMenuItemsFromClosure = (
  id: number,
): Promise<CreateMenuItemsResultDto> =>
  request(`/pos/closure-imports/${id}/create-menu-items`, { method: 'POST' });

// ---------------------------------------------------------------------------
// Supplier price lists + sourcing (Pilastro 4.6)
// ---------------------------------------------------------------------------

export const apiListSupplierPriceLists = (params?: {
  supplier_id?: number;
  active?: boolean;
  in_window?: boolean;
}): Promise<SupplierPriceListDto[]> => {
  const qs = new URLSearchParams();
  if (params?.supplier_id) qs.set('supplier_id', String(params.supplier_id));
  if (params?.active !== undefined) qs.set('active', String(params.active));
  if (params?.in_window !== undefined) qs.set('in_window', String(params.in_window));
  const q = qs.toString();
  return request(`/supplier-price-lists${q ? `?${q}` : ''}`);
};

export const apiCreateSupplierPriceList = (
  input: SupplierPriceListCreateInput,
): Promise<SupplierPriceListDto> =>
  request('/supplier-price-lists', { method: 'POST', body: input });

export const apiUpdateSupplierPriceList = (
  id: number, input: SupplierPriceListUpdateInput,
): Promise<SupplierPriceListDto> =>
  request(`/supplier-price-lists/${id}`, { method: 'PATCH', body: input });

export const apiArchiveSupplierPriceList = (id: number): Promise<void> =>
  request(`/supplier-price-lists/${id}`, { method: 'DELETE' });

export const apiListPriceListItems = (
  listId: number,
): Promise<PriceListItemDto[]> =>
  request(`/supplier-price-lists/${listId}/items`);

export const apiCreatePriceListItem = (
  listId: number, input: PriceListItemCreateInput,
): Promise<PriceListItemDto> =>
  request(`/supplier-price-lists/${listId}/items`, {
    method: 'POST', body: input,
  });

export const apiUpdatePriceListItem = (
  listId: number, itemId: number, input: PriceListItemUpdateInput,
): Promise<PriceListItemDto> =>
  request(`/supplier-price-lists/${listId}/items/${itemId}`, {
    method: 'PATCH', body: input,
  });

export const apiDeletePriceListItem = (
  listId: number, itemId: number,
): Promise<void> =>
  request(`/supplier-price-lists/${listId}/items/${itemId}`, {
    method: 'DELETE',
  });

export const apiBulkCreatePriceListItems = (
  listId: number, lines: PriceListItemCreateInput[],
): Promise<PriceListItemDto[]> =>
  request(`/supplier-price-lists/${listId}/items/bulk`, {
    method: 'POST', body: { lines },
  });

/** Parsing AI di un documento listino (PDF/foto via Claude, xlsx diretto). */
export const apiParsePriceListDocument = (
  file: File,
): Promise<ParsedPriceListDto> => {
  const fd = new FormData();
  fd.append('file', file);
  return request('/supplier-price-lists/parse-document', {
    method: 'POST', body: fd,
  });
};

export const apiSuggestPriceListMatches = (
  listId: number,
): Promise<MatchSuggestionDto[]> =>
  request(`/supplier-price-lists/${listId}/suggest-matches`, {
    method: 'POST',
  });

export const apiApplyPriceListMatches = (
  listId: number,
  assignments: { item_id: number; product_id: number }[],
): Promise<ApplyMatchesResultDto> =>
  request(`/supplier-price-lists/${listId}/apply-matches`, {
    method: 'POST', body: { assignments },
  });

export async function apiImportPriceListCsv(
  listId: number, file: File,
): Promise<PriceListCsvResult> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(
    `${API_BASE_URL}/supplier-price-lists/${listId}/items/import-csv`,
    { method: 'POST', headers, body: formData },
  );
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = data?.detail ?? `HTTP ${res.status}`;
    throw new ApiError(
      res.status,
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      data,
    );
  }
  return data as PriceListCsvResult;
}

export const apiGetProductSourcing = (
  productId: number,
): Promise<ProductSourcingDto> =>
  request(`/sourcing/products/${productId}`);

// ---------------------------------------------------------------------------
// Roles + permissions (Pilastro 5.1)
// ---------------------------------------------------------------------------

export const apiGetPermissionsCatalog = (): Promise<PermissionsCatalogDto> =>
  request('/roles/permissions');

export const apiListRoles = (params?: {
  active?: boolean;
}): Promise<RoleDto[]> => request('/roles', { searchParams: params });

export const apiCreateRole = (input: RoleCreateInput): Promise<RoleDto> =>
  request('/roles', { method: 'POST', body: input });

export const apiUpdateRole = (
  id: number, input: RoleUpdateInput,
): Promise<RoleDto> =>
  request(`/roles/${id}`, { method: 'PATCH', body: input });

export const apiDeleteRole = (id: number): Promise<void> =>
  request(`/roles/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Reviews + AI responder
// ---------------------------------------------------------------------------

export const apiGetReviewsConfig = (): Promise<ReviewReplyConfigDto> =>
  request('/reviews/config');

export const apiUpdateReviewsConfig = (
  input: ReviewReplyConfigUpdateInput,
): Promise<ReviewReplyConfigDto> =>
  request('/reviews/config', { method: 'PATCH', body: input });

export const apiListReviews = (params?: {
  status?: 'pending' | 'drafted' | 'published' | 'skipped';
  rating?: number;
}): Promise<ReviewDto[]> => {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.rating) qs.set('rating', String(params.rating));
  const q = qs.toString();
  return request(`/reviews${q ? `?${q}` : ''}`);
};

export const apiCreateReview = (
  input: ReviewCreateInput,
): Promise<ReviewDto> =>
  request('/reviews', { method: 'POST', body: input });

export const apiGenerateReviewReply = (
  reviewId: number,
): Promise<GeneratedReplyDto> =>
  request(`/reviews/${reviewId}/generate`, { method: 'POST' });

export const apiUpdateReviewReply = (
  reviewId: number, input: ReviewReplyUpdateInput,
): Promise<ReviewDto> =>
  request(`/reviews/${reviewId}`, { method: 'PATCH', body: input });

export const apiMarkReviewPublished = (
  reviewId: number,
): Promise<ReviewDto> =>
  request(`/reviews/${reviewId}/mark-published`, { method: 'POST' });

export const apiSkipReview = (reviewId: number): Promise<ReviewDto> =>
  request(`/reviews/${reviewId}/skip`, { method: 'POST' });

// ---------------------------------------------------------------------------
// Manuale HACCP PDF (Pilastro 5.4)
// ---------------------------------------------------------------------------

export type HaccpManualSection =
  | 'temperatures' | 'batches' | 'cleaning_tasks'
  | 'cleaning_logs' | 'certificates';

/**
 * Apre il PDF Manuale HACCP in una nuova tab.
 * Le sezioni vanno passate come array; vuoto = tutte.
 */
export async function openHaccpManualPdf(params: {
  date_from: string;
  date_to: string;
  sections?: HaccpManualSection[];
}): Promise<void> {
  const qs = new URLSearchParams();
  qs.set('date_from', params.date_from);
  qs.set('date_to', params.date_to);
  if (params.sections && params.sections.length > 0) {
    qs.set('sections', params.sections.join(','));
  }
  const url = `${API_BASE_URL}/haccp/manual.pdf?${qs.toString()}`;
  const token = authStorage.getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { headers });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || `Errore PDF (${res.status})`);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const opened = window.open(blobUrl, '_blank');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  if (!opened) {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `manuale-haccp-${params.date_from}_${params.date_to}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

// ---------------------------------------------------------------------------
// Job roles (ruoli operativi)
// ---------------------------------------------------------------------------

export const apiListJobRoles = (params?: {
  active?: boolean;
  category?: EmployeeDepartment;
}): Promise<JobRoleDto[]> =>
  request('/job-roles', { searchParams: params });

export const apiCreateJobRole = (
  input: JobRoleCreateInput,
): Promise<JobRoleDto> =>
  request('/job-roles', { method: 'POST', body: input });

export const apiUpdateJobRole = (
  id: number,
  input: JobRoleUpdateInput,
): Promise<JobRoleDto> =>
  request(`/job-roles/${id}`, { method: 'PATCH', body: input });

export const apiArchiveJobRole = (id: number): Promise<void> =>
  request(`/job-roles/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Piano ferie (Vacation plan)
// ---------------------------------------------------------------------------

export const apiListVacationWindows = (): Promise<VacationWindowDto[]> =>
  request('/vacation-windows');

export const apiCreateVacationWindow = (
  input: VacationWindowCreateInput,
): Promise<VacationWindowDto> =>
  request('/vacation-windows', { method: 'POST', body: input });

export const apiUpdateVacationWindow = (
  id: number,
  input: VacationWindowUpdateInput,
): Promise<VacationWindowDto> =>
  request(`/vacation-windows/${id}`, { method: 'PATCH', body: input });

export const apiDeleteVacationWindow = (id: number): Promise<void> =>
  request(`/vacation-windows/${id}`, { method: 'DELETE' });

export const apiListVacationRequests = (params?: {
  status?: VacationRequestStatus;
  employee_id?: number;
  mine_only?: boolean;
}): Promise<VacationRequestDto[]> =>
  request('/vacation-requests', { searchParams: params });

export const apiCreateVacationRequest = (
  input: VacationRequestCreateInput,
): Promise<VacationRequestDto> =>
  request('/vacation-requests', { method: 'POST', body: input });

export const apiUpdateVacationRequest = (
  id: number,
  input: VacationRequestUpdateInput,
): Promise<VacationRequestDto> =>
  request(`/vacation-requests/${id}`, { method: 'PATCH', body: input });

export const apiApproveVacationRequest = (
  id: number,
): Promise<VacationRequestDto> =>
  request(`/vacation-requests/${id}/approve`, { method: 'PATCH' });

export const apiRejectVacationRequest = (
  id: number,
  input: VacationRequestDecisionInput,
): Promise<VacationRequestDto> =>
  request(`/vacation-requests/${id}/reject`, {
    method: 'PATCH',
    body: input,
  });

export const apiCancelVacationRequest = (id: number): Promise<void> =>
  request(`/vacation-requests/${id}`, { method: 'DELETE' });

export const apiGetVacationOverlap = (params: {
  from: string;
  to: string;
  department?: EmployeeDepartment | null;
  job_role_id?: number | null;
  include_pending?: boolean;
}): Promise<VacationOverlapResponseDto> =>
  request('/vacation-requests/overlap', {
    searchParams: {
      from: params.from,
      to: params.to,
      ...(params.department ? { department: params.department } : {}),
      ...(params.job_role_id ? { job_role_id: params.job_role_id } : {}),
      ...(params.include_pending !== undefined
        ? { include_pending: params.include_pending }
        : {}),
    },
  });

// ---------------------------------------------------------------------------
// Prenotazioni con caparra (pilastro 8)
// ---------------------------------------------------------------------------

export const apiListReservations = (): Promise<ReservationDto[]> =>
  request('/reservations');

export const apiCreateReservation = (
  input: ReservationInput,
): Promise<ReservationDto> =>
  request('/reservations', { method: 'POST', body: input });

export const apiUpdateReservation = (
  id: number, input: ReservationInput,
): Promise<ReservationDto> =>
  request(`/reservations/${id}`, { method: 'PATCH', body: input });

export const apiDeleteReservation = (id: number): Promise<void> =>
  request(`/reservations/${id}`, { method: 'DELETE' });

export const apiRequestDeposit = (
  id: number, input: { amount: string; terms_text?: string | null },
): Promise<ReservationDto> =>
  request(`/reservations/${id}/request-deposit`, {
    method: 'POST', body: input,
  });

export const apiStripeStatus = (): Promise<StripeStatusDto> =>
  request('/stripe/status');

export const apiStripeConnect = (): Promise<{ url: string }> =>
  request('/stripe/connect', { method: 'POST' });

// ---------------------------------------------------------------------------
// Compiti di cucina
// ---------------------------------------------------------------------------

export const apiListKitchenTasks = (): Promise<KitchenTaskDto[]> =>
  request('/kitchen-tasks');

export const apiCreateKitchenTask = (
  input: KitchenTaskInput,
): Promise<KitchenTaskDto> =>
  request('/kitchen-tasks', { method: 'POST', body: input });

export const apiUpdateKitchenTask = (
  id: number, input: KitchenTaskInput,
): Promise<KitchenTaskDto> =>
  request(`/kitchen-tasks/${id}`, { method: 'PATCH', body: input });

export const apiDeleteKitchenTask = (id: number): Promise<void> =>
  request(`/kitchen-tasks/${id}`, { method: 'DELETE' });

export const apiToggleKitchenTask = (
  id: number, done: boolean,
): Promise<KitchenTaskDto> =>
  request(`/kitchen-tasks/${id}/toggle`, { method: 'POST', body: { done } });

export const apiListKitchenTemplates = (): Promise<KitchenTaskTemplateDto[]> =>
  request('/kitchen-tasks/templates');

export const apiCreateKitchenTemplate = (
  input: KitchenTaskTemplateInput,
): Promise<KitchenTaskTemplateDto> =>
  request('/kitchen-tasks/templates', { method: 'POST', body: input });

export const apiUpdateKitchenTemplate = (
  id: number, input: KitchenTaskTemplateInput,
): Promise<KitchenTaskTemplateDto> =>
  request(`/kitchen-tasks/templates/${id}`, { method: 'PATCH', body: input });

export const apiStopKitchenTemplate = (id: number): Promise<void> =>
  request(`/kitchen-tasks/templates/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Resi / scarti al piatto
// ---------------------------------------------------------------------------

export const apiListDishWaste = (): Promise<DishWasteDto[]> =>
  request('/dish-waste');

export const apiCreateDishWaste = (
  input: DishWasteInput,
): Promise<DishWasteDto> =>
  request('/dish-waste', { method: 'POST', body: input });

export const apiDishWasteSummary = (
  days = 30,
): Promise<DishWasteSummaryItem[]> =>
  request('/dish-waste/summary', { searchParams: { days } });

export const apiDeleteDishWaste = (id: number): Promise<void> =>
  request(`/dish-waste/${id}`, { method: 'DELETE' });

/** OCR di un documento → lista prodotti candidati (non crea nulla). */
export const apiParseProductsDocument = async (
  file: File,
): Promise<ParsedProductsDto> => {
  const form = new FormData();
  form.append('file', file);
  return request('/products/parse-document', { method: 'POST', body: form });
};

/** Crea in blocco i prodotti scelti (salta i nomi già esistenti). */
export const apiBulkCreateProducts = (
  items: BulkProductInput[],
): Promise<BulkProductsResult> =>
  request('/products/bulk', { method: 'POST', body: { items } });

// ---------------------------------------------------------------------------
// Modulo Comande (tavoli + comande a portate + KDS) — gated require_module('cassa')
// ---------------------------------------------------------------------------

/** Tavoli del locale. */
export const apiListTables = (): Promise<DiningTableDto[]> => request('/cassa/tables');

/** Crea un tavolo. */
export const apiCreateTable = (input: {
  number: string;
  room?: string | null;
  seats?: number | null;
}): Promise<DiningTableDto> =>
  request('/cassa/tables', { method: 'POST', body: input });

/** Modifica un tavolo (incl. stato: free/occupied/bill — usato da "Conto"). */
export const apiUpdateTable = (
  tableId: number,
  input: { state?: DiningTableState; room?: string | null; number?: string; seats?: number | null },
): Promise<DiningTableDto> =>
  request(`/cassa/tables/${tableId}`, { method: 'PATCH', body: input });

/** Dettaglio comanda con righe raggruppate per portata. */
export const apiGetOrder = (orderId: number): Promise<OrderDetailDto> =>
  request(`/cassa/orders/${orderId}`);

/** Comande del locale (default solo quelle aperte). */
export const apiListOrders = (onlyOpen = true): Promise<OrderDto[]> =>
  request('/cassa/orders', { searchParams: { only_open: onlyOpen } });

/** Apre una nuova comanda. table_id null/omesso = asporto. */
export const apiCreateOrder = (input: {
  table_id?: number | null;
  covers?: number | null;
  note?: string | null;
}): Promise<OrderDto> =>
  request('/cassa/orders', { method: 'POST', body: input });

/** Aggiunge una voce di menu alla comanda (con portata). */
export const apiAddOrderLine = (
  orderId: number,
  input: { menu_item_id: number; qty?: number; course?: number; note?: string | null },
): Promise<OrderLineDto> =>
  request(`/cassa/orders/${orderId}/lines`, { method: 'POST', body: input });

/** Manda in cucina le portate indicate (pending → fired). */
export const apiFireOrder = (orderId: number, courses: number[]): Promise<unknown> =>
  request(`/cassa/orders/${orderId}/fire`, { method: 'POST', body: { courses } });

/** Avanza lo stato di una riga sul KDS (in_progress→ready→served). */
export const apiUpdateOrderLineState = (
  lineId: number,
  state: OrderLineState,
): Promise<OrderLineDto> =>
  request(`/cassa/kds/lines/${lineId}`, { method: 'PATCH', body: { state } });

/** Riordino uscite: sposta una riga su un'altra portata (perm kds.reorder). */
export const apiReorderLineCourse = (
  lineId: number,
  course: number,
): Promise<OrderLineDto> =>
  request(`/cassa/kds/lines/${lineId}/course`, { method: 'PATCH', body: { course } });

/** Voci esaurite oggi per la location (86 list). */
export const apiListMenuUnavailable = (): Promise<MenuAvailabilityDto[]> =>
  request('/menu-availability');

/** Esaurito (86): segna una voce (in)disponibile oggi (perm menu.availability). */
export const apiSetMenuAvailability = (
  menuItemId: number,
  available: boolean,
): Promise<unknown> =>
  request(`/menu-availability/${menuItemId}`, { method: 'PUT', body: { available } });

/** Chiude la comanda: registra la vendita + scarica magazzino. Ritorna anche le
 *  voci senza distinta che non hanno scaricato (da segnalare all'operatore). */
export const apiCloseOrder = (orderId: number): Promise<CloseResultDto> =>
  request(`/cassa/orders/${orderId}/close`, { method: 'POST' });

/** Registra un incasso parziale (importo + metodo, opz. per prodotto via
 *  line_ids). Al saldo (residuo 0) la comanda si chiude da sola. */
export const apiAddOrderPayment = (
  orderId: number,
  input: OrderPaymentInput,
): Promise<OrderPaymentResultDto> =>
  request(`/cassa/orders/${orderId}/payments`, { method: 'POST', body: input });

/** Annulla un incasso (solo a comanda aperta): riapre il residuo. */
export const apiDeleteOrderPayment = (
  orderId: number,
  paymentId: number,
): Promise<OrderDetailDto> =>
  request(`/cassa/orders/${orderId}/payments/${paymentId}`, { method: 'DELETE' });

/** Storno (C2): pending → subito; già mandata → richiesta di autorizzazione. */
export const apiVoidRequest = (
  orderId: number,
  lineId: number,
  input: { reason_code: string; reason_note?: string | null },
): Promise<VoidRequestResultDto> =>
  request(`/cassa/orders/${orderId}/lines/${lineId}/void-request`, {
    method: 'POST',
    body: input,
  });

/** Richieste di storno (per il KDS del reparto). */
export const apiListVoidRequests = (params?: {
  status?: string;
  department_id?: number;
}): Promise<VoidRequestDto[]> =>
  request('/cassa/void-requests', {
    searchParams: { status: params?.status, department_id: params?.department_id },
  });

/** Il reparto approva una richiesta di storno → la riga va a VOID. */
export const apiApproveVoidRequest = (requestId: number): Promise<VoidRequestDto> =>
  request(`/cassa/void-requests/${requestId}/approve`, { method: 'POST' });

/** Il reparto rifiuta una richiesta di storno → la riga resta nel conto. */
export const apiRejectVoidRequest = (requestId: number): Promise<VoidRequestDto> =>
  request(`/cassa/void-requests/${requestId}/reject`, { method: 'POST' });

/** Registro storni (anti-frode) — filtrabile. Permesso voids.read. */
export const apiListVoids = (params?: {
  from?: string;
  to?: string;
  reason?: string;
  waiter?: number;
  state?: string;
}): Promise<OrderLineVoidDto[]> =>
  request('/cassa/voids', {
    searchParams: {
      from: params?.from,
      to: params?.to,
      reason: params?.reason,
      waiter: params?.waiter,
      state: params?.state,
    },
  });

/** Righe da preparare per il display cucina (monitor unico, tutti i reparti). */
export const apiKdsLines = (): Promise<KdsLineDto[]> => request('/cassa/kds');

/** KDS di un reparto, raggruppato per comanda (bootstrap del realtime). */
export const apiKdsByDepartment = (departmentId: number): Promise<KdsOrderGroupDto[]> =>
  request(`/cassa/kds/${departmentId}`);

/**
 * Reparti selezionabili sul KDS dall'utente corrente. Manager/owner e utenti
 * senza reparti fini configurati vedono tutti i reparti; lo staff con reparti
 * assegnati vede solo i propri (vincolo cucina-vs-bar visibile).
 */
export const apiKdsDepartments = (): Promise<DepartmentDto[]> =>
  request('/cassa/kds/departments');

/** Token effimero (TTL 60s) per aprire lo stream SSE del reparto. */
export const apiKdsStreamToken = (
  departmentId: number,
): Promise<{ token: string; expires_in: number }> =>
  request(`/cassa/kds/${departmentId}/stream-token`, { method: 'POST' });

/** Token effimero per lo stream SSE della SALA (location-wide, cross-reparto). */
export const apiSalaStreamToken = (): Promise<{ token: string; expires_in: number }> =>
  request('/cassa/sala/stream-token', { method: 'POST' });

/** URL assoluto dello stream SSE (l'EventSource non passa per request()). */
export const kdsStreamUrl = (token: string): string =>
  `${API_BASE_URL}/cassa/kds/stream?token=${encodeURIComponent(token)}`;

// --- Entitlement moduli (owner) ---
export const apiListLocationModules = (): Promise<LocationModuleDto[]> =>
  request('/location-modules');

export const apiSetLocationModule = (
  code: string,
  enabled: boolean,
): Promise<LocationModuleDto> =>
  request(`/location-modules/${code}`, { method: 'PUT', body: { enabled } });
